import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  adminNavApi,
  ADMIN_NAV_KEYS,
  POOL_VIEWS,
  type BulkReassignFilter,
  type BulkReassignPreview,
  type BulkReassignApplyResult,
  type BulkReassignNavigator,
  type PoolView,
  type PoolViewCounts,
  type BulkReassignSource,
} from '@/features/navigator/adminNavigation.api';
import { navError } from '@/features/navigator/navigator.api';
import { toast } from '@/stores/toast.store';
import { useAuthStore } from '@/stores/auth.store';
import { useT } from '@/i18n';
import { cn } from '@/lib/utils';
import { NavRebalanceCard } from './NavRebalanceCard';

/**
 * E-36 (Edd, 2026-10-21, propuesta B) · cada pestaña ofrece lo que tiene
 * sentido EN ella. El server no cambia: acepta los mismos valores.
 *
 *  - «Del buzón»: el server fuerza el buzón (E-35d, `soloPool`), así que
 *    `open` era «Todo el buzón» con otro nombre (los dos decían 13) y parecía
 *    «todo el sistema». Sale de acá.
 *  - «De una persona»: el filtro se aplica sobre los casos de ESA persona.
 *    `in_pool` exige dueño 'pool', así que para una persona da siempre 0 — no
 *    se ofrece. `open` sí: son todos sus abiertos, y es lo que hace falta para
 *    vaciar la cuenta de una baja (decisión del coordinador E-36).
 */
export const FILTERS_POOL: BulkReassignFilter[] = [
  'findhelp_positives',
  'findhelp_negatives',
  // POOL-B2 (D-1): «En el buzón» reemplaza al filtro muerto «Sin asignar».
  'in_pool',
  'somos',
];
export const FILTERS_PERSON: BulkReassignFilter[] = [
  'findhelp_positives',
  'findhelp_negatives',
  'open',
  'somos',
];

/**
 * D-3 · lo único que un closer puede recibir — espejo de
 * `CLOSER_ALLOWED_FILTERS` del server (nav-bulk-reassign.service.ts). El
 * candado de verdad es el 400 CLOSER_ONLY_NEGATIVES; esto avisa ANTES.
 */
const CLOSER_FILTERS: BulkReassignFilter[] = ['findhelp_negatives', 'pool_negatives'];

export function closerPuedeRecibir(filter: BulkReassignFilter): boolean {
  return CLOSER_FILTERS.includes(filter);
}

// El server acepta hasta 500 por tanda (MAX_LIMIT del servicio) — el slider
// nunca ofrece más que min(piscina, 500).
const SERVER_MAX = 500;

// F5-P2 · «Pasar TODOS» — el mismo mecanismo del módulo de agentes (R7):
// tandas que RE-LISTAN elegibles frescos en cada vuelta y un freno absurdo-alto
// para que un bug nunca se convierta en un bucle infinito sobre data real.
const TANDA = 200;
const FRENO = 5000;

/** F5-P2 — modo del formulario: por filtro (lo de siempre) o por origen. */
type ReassignMode = 'filter' | 'origin';

function asNavigatorList(data: unknown): BulkReassignNavigator[] {
  return Array.isArray(data) ? (data as BulkReassignNavigator[]) : [];
}

function asSourceList(data: unknown): BulkReassignSource[] {
  return Array.isArray(data) ? (data as BulkReassignSource[]) : [];
}

type ApplyBlockReason =
  | 'needNavigator'
  | 'needReason'
  | 'needPreview'
  | 'previewStale'
  | 'nothingToAssign';

function resolveApplyBlockReason(input: {
  navigatorId: string;
  limit: number;
  reason: string;
  preview: BulkReassignPreview | null;
  filter: BulkReassignFilter;
  /** F5-P2: '' cuando se reparte por filtro. */
  fromNavigatorId: string;
}): ApplyBlockReason | null {
  if (!input.navigatorId || input.limit < 1) return 'needNavigator';
  if (input.reason.trim().length < 3) return 'needReason';
  if (input.preview == null) return 'needPreview';
  if (
    input.preview.filter !== input.filter ||
    String(input.preview.navigatorId) !== String(input.navigatorId) ||
    Number(input.preview.limit) !== Number(input.limit) ||
    // F5-P2: cambiar de origen (o salir del modo origen) vence el preview.
    String(input.preview.fromNavigatorId ?? '') !== String(input.fromNavigatorId)
  ) {
    return 'previewStale';
  }
  if (input.preview.willAssign <= 0) return 'nothingToAssign';
  return null;
}

/**
 * F5-R4a — un radio del formulario de reasignación (dibujo: filtro y destino
 * son listas de tarjetas-radio, con el dato ANTES de elegir).
 */
function RadioCard({
  on, onPick, children, disabled,
}: {
  on: boolean;
  onPick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={on}
      disabled={disabled}
      onClick={onPick}
      className={cn(
        'flex items-center gap-[11px] w-full box-border min-h-[48px] px-3.5 py-2.5 rounded-[10px] border-[1.5px] text-left transition-colors duration-150 motion-reduce:transition-none',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal',
        on ? 'border-nav-teal bg-nav-tealTint/40' : 'border-nav-card bg-white hover:border-nav-teal hover:bg-nav-tealTint/40',
        disabled && 'opacity-60 cursor-not-allowed',
      )}
    >
      <span className={cn(
        'flex-none inline-flex items-center justify-center w-[18px] h-[18px] rounded-full border-[1.5px] bg-white',
        on ? 'border-nav-teal' : 'border-nav-line',
      )}>
        <span className={cn('w-[9px] h-[9px] rounded-full', on ? 'bg-nav-teal' : 'bg-transparent')} />
      </span>
      {children}
    </button>
  );
}

/**
 * El número de cada opción, ANTES de elegirla (endpoints sin PHI).
 *
 * E-36 · el número tiene que ser de quien dice ser:
 *  - «Del buzón»: `pool-count`, que el server cuenta SOLO en el buzón
 *    (E-35d) → «N en el buzón».
 *  - «De una persona»: ese número del buzón NO es de la persona, así que no
 *    se muestra. Con una persona elegida se muestra el SUYO (`source-count`,
 *    abiertos de ella con ese filtro); sin persona elegida, nada.
 */
function FilterChip({
  filter, personMode, personId,
}: {
  filter: BulkReassignFilter;
  personMode: boolean;
  personId: string;
}): JSX.Element | null {
  const t = useT();
  const poolQ = useQuery({
    queryKey: ADMIN_NAV_KEYS.bulkPool(filter),
    queryFn: () => adminNavApi.bulkReassignPoolCount(filter),
    enabled: !personMode,
  });
  const personQ = useQuery({
    queryKey: ADMIN_NAV_KEYS.bulkSourceCount(personId, filter),
    queryFn: () => adminNavApi.bulkReassignSourceCount(personId, filter),
    enabled: personMode && Boolean(personId),
  });
  if (personMode && !personId) return null;
  const q = personMode ? personQ : poolQ;
  return (
    <span
      data-testid={`e36-chip-${filter}`}
      className="flex-none rounded-full px-2.5 py-[3px] bg-nav-tealTint text-nav-tealDark border border-[#c9e2e2] font-head text-[11px] font-bold tabular-nums whitespace-nowrap"
    >
      {q.isLoading
        ? <Loader2 className="h-3 w-3 animate-spin inline" aria-hidden />
        : q.isError
          ? t('f5r4a.re.poolErr')
          : personMode
            ? t('e36.re.chipPerson', { n: personQ.data?.openCases ?? 0 })
            : t('f5r4a.re.pool', { n: poolQ.data?.poolCount ?? 0 })}
    </span>
  );
}

/** E-36 · una insignia corta (Activa / De baja / Navegadora / Closer). */
function Badge({
  tone, children,
}: {
  tone: 'ok' | 'off' | 'nav' | 'closer';
  children: React.ReactNode;
}): JSX.Element {
  return (
    <span
      className={cn(
        'inline-block ml-1.5 align-[1px] rounded-md px-1.5 py-[1px] font-head text-[10.5px] font-extrabold whitespace-nowrap',
        tone === 'ok' && 'bg-nav-sageTint text-nav-sageText',
        tone === 'off' && 'bg-nav-redTint text-nav-red',
        tone === 'nav' && 'bg-nav-soft text-nav-navy border border-nav-card',
        tone === 'closer' && 'bg-nav-amberTint text-nav-amberText',
      )}
    >
      {children}
    </span>
  );
}

/**
 * F5-P3 · Las cuatro vistas de la piscina, con su conteo real (firma CEO
 * 2026-08-30). Van arriba del formulario porque son el paso 0 de repartir:
 * primero se mira en qué estado está la piscina, después se elige a quién.
 *
 * «Esperando verificación» va inerte a propósito — la firma dice *No
 * repartir*, y el candado de verdad es el 400 del server
 * (POOL_VIEW_NOT_DEALABLE). Acá se muestra el número, que es lo que le sirve
 * al CEO: cuánta cola le está frenando el uploader. Mismo patrón de píldora
 * apagada-con-conteo-vivo que ya usa la pestaña Casos.
 */
function PoolViewChips({
  counts, isLoading, isError, value, onPick,
}: {
  counts: PoolViewCounts | undefined;
  isLoading: boolean;
  isError: boolean;
  value: BulkReassignFilter;
  onPick: (v: PoolView) => void;
}): JSX.Element {
  const t = useT();
  const countOf = (v: PoolView): number | null => {
    if (!counts) return null;
    if (v === 'pool_awaiting_verification') return counts.awaitingVerification;
    if (v === 'pool_ready_to_deal') return counts.readyToDeal;
    if (v === 'pool_no_medicaid') return counts.noMedicaid;
    return counts.negatives;
  };
  const dealable = (v: PoolView): boolean => v !== 'pool_awaiting_verification';

  return (
    <section className="rounded-xl border-[1.5px] border-nav-card bg-white p-[22px]">
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3.5">
        <p className="font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-teal">
          {t('f5p3.pool.kicker')}
        </p>
        {counts && (
          <span className="text-[12px] text-nav-slate tabular-nums">
            {t('f5p3.pool.total', { n: counts.inPool })}
          </span>
        )}
      </div>
      <p className="text-[13px] leading-relaxed text-nav-slate mb-4">{t('f5p3.pool.intro')}</p>

      <div className="flex gap-2 flex-wrap">
        {POOL_VIEWS.map((v) => {
          const on = value === v;
          const live = dealable(v);
          const n = countOf(v);
          return (
            <button
              key={v}
              type="button"
              disabled={!live}
              aria-pressed={live ? on : undefined}
              aria-disabled={!live || undefined}
              title={live ? undefined : t('f5p3.pool.noDeal')}
              onClick={live ? () => onPick(v) : undefined}
              className={cn(
                'inline-flex items-center gap-2 min-h-[44px] px-[15px] rounded-lg border-[1.5px] font-head text-[12.5px] font-bold transition-colors duration-150 motion-reduce:transition-none',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal',
                !live
                  ? 'bg-white border-nav-card text-nav-faint cursor-not-allowed'
                  : on
                    ? 'bg-nav-navy border-nav-navy text-white hover:bg-nav-navyHover'
                    : 'bg-white border-nav-line text-nav-navy hover:border-nav-teal hover:text-nav-tealDark hover:bg-nav-tealTint/40',
              )}
            >
              {t(`f5p3.pool.view.${v}` as 'f5p3.pool.view.pool_ready_to_deal')}
              <span className={cn(
                'tabular-nums font-semibold',
                live && on ? 'text-white/75' : 'text-nav-faint',
              )}>
                {isLoading
                  ? <Loader2 className="h-3 w-3 animate-spin inline" aria-hidden />
                  : isError || n === null ? '—' : n}
              </span>
            </button>
          );
        })}
      </div>
      <p className="text-[11px] text-nav-slate mt-2">{t('f5p3.pool.noDealNote')}</p>
      {isError && (
        <p className="text-xs text-nav-red mt-1.5" role="alert">{t('f5p3.pool.err')}</p>
      )}
    </section>
  );
}

/**
 * Admin → Navigation → Reasignación (GO CEO A 2026-08-26).
 * Preview candidates → confirm → assign batch. Assignment columns only.
 *
 * F5-R4a — la estructura del dibujo, con el GATE INTACTO (regla 2):
 * (1) el slider tiene el tope de la piscina — el aviso «la cantidad supera la
 *     piscina» deja de existir porque el estado que avisaba ya no es posible;
 * (2) cada filtro muestra su piscina ANTES de elegir;
 * (3) el destino lista la carga actual de cada navigator;
 * (4) el Aplicar no existe hasta correr el preview — y el preview dice por
 *     qué está inerte (falta motivo, falta destino, piscina en 0);
 * (5) los salteados con razón son parte del preview (los agregados que el
 *     server ya devuelve; el detalle por persona NECESITA-SERVER).
 * resolveApplyBlockReason y las mutaciones no cambian una línea.
 *
 * E-36 (Edd, 2026-10-21, propuesta B) · la pantalla dice la verdad y gana
 * «Repartir parejo» arriba (solo admin). El server ya era seguro desde E-35d;
 * lo que cambia son textos, insignias y avisos previos. Ni el gate ni las
 * mutaciones cambian.
 */
export function NavBulkReassignTab(): JSX.Element {
  const t = useT();
  const qc = useQueryClient();
  // E-36: «Repartir parejo» solo para el admin — el POST rebalance es
  // `@Roles('admin')`; el supervisor ve esta pestaña pero no la tarjeta.
  const isAdmin = useAuthStore((s) => s.user?.role) === 'admin';
  const [filter, setFilter] = useState<BulkReassignFilter>('findhelp_positives');
  const [navigatorId, setNavigatorId] = useState('');
  const [limit, setLimit] = useState(50);
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState<BulkReassignPreview | null>(null);
  // F5-P2 — el camino por ORIGEN (encargo CEO 2026-08-30).
  const [mode, setMode] = useState<ReassignMode>('filter');
  const [fromNavigatorId, setFromNavigatorId] = useState('');
  const [allBusy, setAllBusy] = useState(false);
  const [allProg, setAllProg] = useState<{ done: number; total: number } | null>(null);
  const [allConfirm, setAllConfirm] = useState(false);

  const navigatorsQ = useQuery({
    queryKey: ADMIN_NAV_KEYS.bulkNavigators,
    queryFn: () => adminNavApi.bulkReassignNavigators(),
  });
  const navigators = asNavigatorList(navigatorsQ.data);

  // F5-P2: los orígenes son otra lista — entran los DADOS DE BAJA con carga
  // (vaciar esas cuentas es el caso de uso) y el buzón. El destino no cambia.
  const sourcesQ = useQuery({
    queryKey: ADMIN_NAV_KEYS.bulkSources,
    queryFn: () => adminNavApi.bulkReassignSources(),
    enabled: mode === 'origin',
  });
  // E-36: el buzón sale de esta lista — ya tiene su pestaña («Del buzón»).
  const sources = asSourceList(sourcesQ.data).filter(
    (s) => s.navigatorScope !== 'pool' && String(s.navigatorId) !== String(navigatorId),
  );

  const sourceCountQ = useQuery({
    queryKey: ADMIN_NAV_KEYS.bulkSourceCount(fromNavigatorId, filter),
    queryFn: () => adminNavApi.bulkReassignSourceCount(fromNavigatorId, filter),
    enabled: mode === 'origin' && Boolean(fromNavigatorId),
  });
  const sourceCount = sourceCountQ.data ?? null;

  const poolQ = useQuery({
    queryKey: ADMIN_NAV_KEYS.bulkPool(filter),
    queryFn: () => adminNavApi.bulkReassignPoolCount(filter),
  });
  const poolCount = poolQ.data?.poolCount ?? null;

  // F5-P3 — los cuatro conteos de la piscina, UNA llamada para los cuatro.
  const poolViewsQ = useQuery({
    queryKey: ADMIN_NAV_KEYS.bulkPoolViews,
    queryFn: () => adminNavApi.bulkReassignPoolViews(),
  });

  // (1) El tope del slider ES la piscina (capada al máximo del server).
  // F5-P2: con origen elegido, el tope es lo que ESE navigator tiene movible —
  // «como máximo lo que tenga disponible» (verbatim del encargo).
  const originMode = mode === 'origin';
  const disponible = originMode ? sourceCount?.movableCases ?? null : poolCount;
  const poolMax =
    disponible === null ? SERVER_MAX : Math.max(1, Math.min(disponible, SERVER_MAX));
  const effLimit = Math.min(limit, poolMax);
  const fromParam = originMode && fromNavigatorId ? fromNavigatorId : undefined;

  const previewMut = useMutation({
    mutationFn: () =>
      adminNavApi.bulkReassignPreview({
        filter,
        navigatorId,
        limit: effLimit,
        reason: reason.trim() || undefined,
        fromNavigatorId: fromParam,
      }),
    onSuccess: (data) => {
      setPreview(data);
      toast.success(t('adminNav.bulkReassign.previewDone'));
    },
    onError: (e) => {
      setPreview(null);
      toast.error(t('adminNav.bulkReassign.failed'), navError(e).message ?? '');
    },
  });

  const applyMut = useMutation({
    mutationFn: () =>
      adminNavApi.bulkReassignApply({
        filter,
        navigatorId,
        limit: effLimit,
        reason: reason.trim(),
        fromNavigatorId: fromParam,
      }),
    onSuccess: (data: BulkReassignApplyResult) => {
      setPreview(data);
      toast.success(t('adminNav.bulkReassign.applyDone', { count: data.applied }));
      void qc.invalidateQueries({ queryKey: ['admin', 'navigation'] });
      void qc.invalidateQueries({ queryKey: ADMIN_NAV_KEYS.bulkNavigators });
      void qc.invalidateQueries({ queryKey: ADMIN_NAV_KEYS.bulkPool(filter) });
      void qc.invalidateQueries({ queryKey: ADMIN_NAV_KEYS.bulkSources });
    },
    onError: (e) => toast.error(t('adminNav.bulkReassign.failed'), navError(e).message ?? ''),
  });

  /**
   * F5-P2 «Pasar TODOS» — copiado del R7 de agentes: tandas que RE-CUENTAN lo
   * disponible en cada vuelta (así las exclusiones viajan gratis y una cita
   * agendada en el medio deja de moverse sola), progreso visible, verificación
   * final y freno de seguridad. La confirmación con el número exacto va ANTES.
   */
  const pasarTodos = async (): Promise<void> => {
    if (!originMode || !fromNavigatorId || !navigatorId || allBusy) return;
    if (reason.trim().length < 3) {
      toast.error(t('f5p2.re.needReason'));
      return;
    }
    setAllBusy(true);
    setAllConfirm(false);
    let done = 0;
    const objetivo = sourceCount?.movableCases ?? 0;
    setAllProg({ done: 0, total: objetivo });
    try {
      for (;;) {
        const vuelta = await adminNavApi.bulkReassignSourceCount(fromNavigatorId, filter);
        if (vuelta.movableCases <= 0) break;
        const res = await adminNavApi.bulkReassignApply({
          filter,
          navigatorId,
          limit: Math.min(TANDA, vuelta.movableCases),
          reason: reason.trim(),
          fromNavigatorId,
        });
        // Si una tanda no mueve nada, parar: seguir sería un bucle.
        if (res.applied <= 0) break;
        done += res.applied;
        setAllProg({ done, total: Math.max(objetivo, done) });
        if (done > FRENO) break;
      }
      // Verificación final: qué le quedó al origen, medido, no supuesto.
      const resto = await adminNavApi.bulkReassignSourceCount(fromNavigatorId, filter);
      if (resto.movableCases === 0) {
        toast.success(t('f5p2.all.done', { n: done }));
      } else {
        toast.info(t('f5p2.all.partial', { n: done, resto: resto.movableCases }));
      }
    } catch (e) {
      toast.error(t('adminNav.bulkReassign.failed'), navError(e).message ?? '');
    } finally {
      setAllBusy(false);
      setAllProg(null);
      setPreview(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'navigation'], refetchType: 'all' });
      void qc.invalidateQueries({ queryKey: ADMIN_NAV_KEYS.bulkSources });
      void sourceCountQ.refetch();
    }
  };

  // D-3: un closer solo recibe tandas negativas. El candado REAL es el 400 del
  // server (CLOSER_ONLY_NEGATIVES). E-36: en vez de avisar DESPUÉS de elegirlo,
  // los closers salen deshabilitados con su pista mientras el filtro no sea
  // de negativos (`closerPuedeRecibir`, espejo de la lista del server — la
  // vista 4 de la piscina también garantiza negativos y también vale).
  const selectedNavigator = navigators.find((n) => String(n.navigatorId) === String(navigatorId));

  /**
   * E-36 · cambiar de filtro. Si el destino elegido es un closer y el filtro
   * nuevo no es de negativos, se suelta el destino: dejarlo marcado sería
   * dejar marcada una opción deshabilitada que el server rebotaría.
   */
  const pickFilter = (f: BulkReassignFilter): void => {
    setFilter(f);
    setPreview(null);
    if (selectedNavigator?.navigatorScope === 'closer' && !closerPuedeRecibir(f)) {
      setNavigatorId('');
    }
  };

  /** Cambiar de pestaña. Por dentro siguen los mismos dos modos. */
  const pickMode = (m: ReassignMode): void => {
    setMode(m);
    setPreview(null);
    setAllConfirm(false);
    if (m === 'filter') {
      setFromNavigatorId('');
      // `open` no existe en «Del buzón» (E-36): se vuelve a la primera opción.
      if (!FILTERS_POOL.includes(filter) && !POOL_VIEWS.includes(filter as PoolView)) {
        pickFilter('findhelp_positives');
      }
    } else {
      // El camino por persona arranca en «Todo lo de esta persona»: el
      // encargo es vaciar la cuenta, no filtrarla.
      pickFilter('open');
    }
  };

  const canPreview =
    Boolean(navigatorId)
    && effLimit >= 1
    && navigators.length > 0
    && (!originMode || Boolean(fromNavigatorId));
  const applyBlockReason = resolveApplyBlockReason({
    navigatorId,
    limit: effLimit,
    reason,
    preview,
    filter,
    fromNavigatorId: fromParam ?? '',
  });
  const canApply = canPreview && applyBlockReason == null;

  // (4) El preview dice por qué está inerte.
  const previewWhy = originMode && !fromNavigatorId
    ? t('f5p2.re.why.source')
    : !navigatorId
      ? t('f5r4a.re.why.dest')
      : reason.trim().length < 3
        ? t('f5r4a.re.why.reason')
        : disponible === 0
          ? (originMode ? t('f5p2.re.why.empty') : t('f5r4a.re.why.pool'))
          : null;
  const canRunPreview =
    canPreview && previewWhy == null && !previewMut.isPending && !allBusy;

  // El panel de la derecha muestra el preview SOLO si sigue al día con los
  // parámetros (mismas comparaciones que el gate 'previewStale').
  const previewFresh = preview != null
    && preview.filter === filter
    && String(preview.navigatorId) === String(navigatorId)
    && Number(preview.limit) === Number(effLimit)
    && String(preview.fromNavigatorId ?? '') === String(fromParam ?? '');

  const destName = selectedNavigator?.navigatorName ?? preview?.navigatorName ?? '';

  // E-36: ¿el origen elegido está de baja? Manda lo que contó el server para
  // ese origen; si todavía no contestó, lo que dice la lista de orígenes.
  const fromSource = sources.find((s) => String(s.navigatorId) === String(fromNavigatorId));
  const fromInactive = sourceCount != null
    ? sourceCount.fromNavigatorActive === false
    : fromSource?.active === false;

  return (
    <div className="flex flex-col gap-4 font-body text-nav-navy">
      {/* E-36 · «Repartir parejo», arriba de todo (solo admin). */}
      {isAdmin && <NavRebalanceCard />}

      {/* ------------------------------ 0 · La piscina, en sus cuatro vistas */}
      <PoolViewChips
        counts={poolViewsQ.data}
        isLoading={poolViewsQ.isLoading}
        isError={poolViewsQ.isError}
        value={filter}
        onPick={(v) => {
          // E-36: las cuatro vistas son del BUZÓN. Para una persona darían
          // siempre 0, así que elegir una vista lleva a «Del buzón».
          if (originMode) {
            setMode('filter');
            setFromNavigatorId('');
            setAllConfirm(false);
          }
          pickFilter(v);
        }}
      />

      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))' }}>
      {/* ------------------------------------------------ 1 · Mover a mano */}
      <section className="rounded-xl border-[1.5px] border-nav-card bg-white p-[22px]">
        <p className="font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-teal mb-3.5">
          {t('f5r4a.re.k1')}
        </p>

        {/* F5-P2 — dos caminos. E-36: se llaman «Del buzón» y «De una persona». */}
        <div
          role="radiogroup"
          aria-label={t('f5p2.re.mode')}
          className="inline-flex flex-wrap items-center gap-[2px] bg-nav-soft border-[1.5px] border-nav-card rounded-[10px] p-[3px] mb-3"
        >
          {(['filter', 'origin'] as ReassignMode[]).map((m) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={mode === m}
              onClick={() => pickMode(m)}
              className={cn(
                'inline-flex items-center min-h-[40px] px-3.5 rounded-lg font-head text-[12.5px] font-bold transition-colors duration-150 motion-reduce:transition-none',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal',
                mode === m
                  ? 'bg-nav-navy text-white'
                  : 'bg-transparent text-nav-slate hover:bg-white hover:text-nav-navy',
              )}
            >
              {m === 'filter' ? t('f5p2.re.modeFilter') : t('f5p2.re.modeOrigin')}
            </button>
          ))}
        </div>
        {/* E-36 · el subtítulo según la pestaña. Reemplaza a «solo cambian
            campos de asignación…», que era falso para las bajas. */}
        <p className="text-[13px] leading-relaxed text-nav-slate mb-5" data-testid="e36-subtitle">
          {originMode ? t('e36.re.subPerson') : t('e36.re.subPool')}
        </p>

        {originMode && (
          <>
            <span className="block font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate">
              {t('f5p2.re.source')}
            </span>
            <p className="text-[12.5px] leading-relaxed text-nav-slate mt-1.5 mb-2">
              {t('f5p2.re.sourceHint')}
            </p>
            <div role="radiogroup" aria-label={t('f5p2.re.source')} className="flex flex-col gap-2 mb-2">
              {sourcesQ.isLoading ? (
                <p className="text-sm text-nav-slate flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  {t('adminNav.bulkReassign.navigatorLoading')}
                </p>
              ) : sourcesQ.isError ? (
                <p className="text-xs text-nav-red" role="alert">
                  {t('adminNav.bulkReassign.navigatorError')}{' '}
                  <button type="button" className="underline" onClick={() => void sourcesQ.refetch()}>
                    {t('adminNav.bulkReassign.retry')}
                  </button>
                </p>
              ) : sources.length === 0 ? (
                <p className="text-sm text-nav-slate">{t('f5p2.re.sourceEmpty')}</p>
              ) : (
                sources.map((s) => (
                  <RadioCard
                    key={s.navigatorId}
                    on={String(fromNavigatorId) === String(s.navigatorId)}
                    disabled={allBusy}
                    onPick={() => {
                      setFromNavigatorId(String(s.navigatorId));
                      setPreview(null);
                      setAllConfirm(false);
                    }}
                  >
                    {/* E-36: activa o de baja se dice EN la fila, con lo que
                        eso significa: de una activa solo salen los nunca
                        contactados; de una de baja sale todo (E-35d). */}
                    <span className="flex-1 min-w-0" data-testid={`e36-source-${s.navigatorId}`}>
                      <span className="block font-head text-[13.5px] font-bold text-nav-navy">
                        {s.navigatorName}
                        {s.active
                          ? <Badge tone="ok">{t('e36.re.badgeActive')}</Badge>
                          : <Badge tone="off">{t('e36.re.badgeInactive')}</Badge>}
                      </span>
                      <span className="block mt-0.5 text-[12px] text-nav-slate tabular-nums">
                        {s.active
                          ? t('e36.re.srcActive', { n: s.openEpisodes })
                          : t('e36.re.srcInactive', { n: s.openEpisodes })}
                      </span>
                    </span>
                  </RadioCard>
                ))
              )}
            </div>

            {/* «N de M»: lo movible sobre lo abierto, con cada exclusión contada. */}
            {fromNavigatorId && (
              <div className="rounded-[10px] border-[1.5px] border-nav-card bg-nav-soft px-3.5 py-3 mb-5">
                {sourceCountQ.isLoading ? (
                  <p className="m-0 text-[12.5px] text-nav-slate flex items-center gap-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                    {t('f5p2.re.counting')}
                  </p>
                ) : sourceCountQ.isError ? (
                  <p className="m-0 text-xs text-nav-red" role="alert">
                    {t('f5p2.re.countErr')}{' '}
                    <button type="button" className="underline" onClick={() => void sourceCountQ.refetch()}>
                      {t('adminNav.bulkReassign.retry')}
                    </button>
                  </p>
                ) : sourceCount ? (
                  <>
                    <p className="m-0 font-head text-[13.5px] font-bold text-nav-navy tabular-nums">
                      {t('f5p2.re.available', {
                        n: sourceCount.movableCases,
                        m: sourceCount.openCases,
                      })}
                    </p>
                    <p className="m-0 mt-0.5 text-[12px] text-nav-slate tabular-nums">
                      {t('f5p2.re.members', { n: sourceCount.movableClients })}
                    </p>
                    {sourceCount.skippedCases > 0 && (
                      <ul className="m-0 mt-2 p-0 list-none flex flex-col gap-1 text-[12px] text-nav-amberText tabular-nums">
                        {/* E-36: el motivo principal de una persona ACTIVA —
                            lo trabajado se queda con ella. Antes no se decía:
                            el «N de M» bajaba sin explicación. */}
                        {(sourceCount.skippedByReason.MEMBER_HAS_WORK ?? 0) > 0 && (
                          <li>
                            {t('e36.re.stayWorked', {
                              name: sourceCount.fromNavigatorName,
                              n: sourceCount.skippedByReason.MEMBER_HAS_WORK,
                            })}
                          </li>
                        )}
                        {sourceCount.skippedByReason.NAV_APPOINTMENT_SCHEDULED > 0 && (
                          <li>
                            {t('f5p2.skip.NAV_APPOINTMENT_SCHEDULED', {
                              n: sourceCount.skippedByReason.NAV_APPOINTMENT_SCHEDULED,
                            })}
                          </li>
                        )}
                        {sourceCount.skippedByReason.FOLLOWUP_ACCEPTED_PENDING > 0 && (
                          <li>
                            {t('f5p2.skip.FOLLOWUP_ACCEPTED_PENDING', {
                              n: sourceCount.skippedByReason.FOLLOWUP_ACCEPTED_PENDING,
                            })}
                          </li>
                        )}
                        {sourceCount.skippedByReason.OPEN_SESSION > 0 && (
                          <li>
                            {t('f5p2.skip.OPEN_SESSION', {
                              n: sourceCount.skippedByReason.OPEN_SESSION,
                            })}
                          </li>
                        )}
                        {sourceCount.skippedByReason.MEMBER_SPLIT_BY_FILTER > 0 && (
                          <li>
                            {t('f5p2.skip.MEMBER_SPLIT_BY_FILTER', {
                              n: sourceCount.skippedByReason.MEMBER_SPLIT_BY_FILTER,
                            })}
                          </li>
                        )}
                        <li className="opacity-80">{t('f5p2.re.wholeMember')}</li>
                      </ul>
                    )}
                  </>
                ) : null}
              </div>
            )}
          </>
        )}

        <span className="block font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate">
          {t('f5r4a.re.filter')}
        </span>
        <div role="radiogroup" aria-label={t('f5r4a.re.filter')} className="flex flex-col gap-2 mt-2 mb-2">
          {(originMode ? FILTERS_PERSON : FILTERS_POOL).map((f) => (
            <RadioCard
              key={f}
              on={filter === f}
              onPick={() => pickFilter(f)}
            >
              <span className="flex-1 min-w-0 text-[13.5px]">
                {t(`adminNav.bulkReassign.filter.${f}` as 'adminNav.bulkReassign.filter.open')}
              </span>
              {/* (2) El número de cada opción, antes de elegir — y de quien
                  dice ser (E-36: el buzón, o la persona elegida). */}
              <FilterChip filter={f} personMode={originMode} personId={fromNavigatorId} />
            </RadioCard>
          ))}
        </div>
        {/* F5-P3: con una vista de la piscina elegida arriba, ninguna de las
            opciones de siempre queda marcada — se dice, no se deja adivinar. */}
        {POOL_VIEWS.includes(filter as PoolView) && (
          <p className="text-[12px] text-nav-tealDark mb-2" role="status">
            {t('f5p3.re.fromView', {
              name: t(`f5p3.pool.view.${filter as PoolView}` as 'f5p3.pool.view.pool_ready_to_deal'),
            })}
          </p>
        )}

        <span className="block mt-5 font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate">
          {t('f5r4a.re.dest')}
        </span>
        <div role="radiogroup" aria-label={t('f5r4a.re.dest')} className="flex flex-col gap-2 mt-2 mb-2">
          {navigatorsQ.isLoading ? (
            <p className="text-sm text-nav-slate flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              {t('adminNav.bulkReassign.navigatorLoading')}
            </p>
          ) : navigators.length === 0 ? (
            <p className="text-sm text-nav-slate">{t('adminNav.bulkReassign.navigatorEmpty')}</p>
          ) : (
            navigators.map((n) => {
              const isCloser = n.navigatorScope === 'closer';
              // E-36: un closer con un filtro que no es de negativos sale
              // deshabilitado, con la pista — el server lo rebotaría igual.
              const closerBlocked = isCloser && !closerPuedeRecibir(filter);
              return (
              <RadioCard
                key={n.navigatorId}
                on={String(navigatorId) === String(n.navigatorId)}
                disabled={closerBlocked}
                onPick={() => {
                  setNavigatorId(String(n.navigatorId));
                  setPreview(null);
                  // F5-P2: nadie se reasigna a sí mismo — elegir de destino al
                  // que ya era origen suelta el origen (el server igual rebota
                  // con SAME_NAVIGATOR; esto evita llegar a chocarlo).
                  if (String(fromNavigatorId) === String(n.navigatorId)) {
                    setFromNavigatorId('');
                    setAllConfirm(false);
                  }
                }}
              >
                <span className="flex-1 min-w-0" data-testid={`e36-dest-${n.navigatorId}`}>
                  <span className="block font-head text-[13.5px] font-bold text-nav-navy">
                    {n.navigatorName}
                    {/* E-36: el tipo, según `navigator_scope`. */}
                    {isCloser
                      ? <Badge tone="closer">{t('e36.re.badgeCloser')}</Badge>
                      : <Badge tone="nav">{t('e36.re.badgeNav')}</Badge>}
                  </span>
                  {/* (3) La carga actual, visible antes de decidir. */}
                  <span className="block mt-0.5 text-[12px] text-nav-slate tabular-nums">
                    {t('f5r4a.re.openLoad', { n: n.openEpisodes })}
                  </span>
                  {closerBlocked && (
                    <span className="block mt-0.5 text-[12px] text-nav-amberText">
                      {t('e36.re.closerOnlyNeg')}
                    </span>
                  )}
                </span>
              </RadioCard>
              );
            })
          )}
        </div>
        {navigatorsQ.isError && (
          <p className="text-xs text-nav-red mb-2" role="alert">
            {t('adminNav.bulkReassign.navigatorError')}{' '}
            <button
              type="button"
              className="underline"
              onClick={() => void navigatorsQ.refetch()}
            >
              {t('adminNav.bulkReassign.retry')}
            </button>
          </p>
        )}

        <span className="block font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate mt-4">
          {t('f5r4a.re.qty')}
        </span>
        <div className="flex items-center gap-3 mt-2.5">
          <input
            type="range"
            min={1}
            max={poolMax}
            value={effLimit}
            /* E-36: lo que hay DONDE se está mirando (el buzón o la persona).
               Antes miraba siempre el buzón: con el buzón vacío se apagaba
               también el reparto desde una persona. */
            disabled={disponible === 0}
            aria-label={t('f5r4a.re.qtyAria')}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPreview(null);
            }}
            className="flex-1 min-w-[160px] h-[44px] accent-nav-teal disabled:opacity-50"
          />
          <span className="flex-none min-w-[56px] text-center bg-nav-tealTint/40 border-[1.5px] border-[#c9e2e2] rounded-lg py-[9px] font-head text-[15px] font-extrabold text-nav-tealDark tabular-nums">
            {effLimit}
          </span>
        </div>
        <p className="text-[12.5px] leading-relaxed text-nav-slate mt-2.5">
          {t('f5r4a.re.qtyNote', { max: poolMax })}
          {disponible !== null && disponible > SERVER_MAX ? ` ${t('f5r4a.re.qtyCap500')}` : ''}
        </p>

        <label className="block mt-5">
          <span className="block font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate">
            {t('f5r4a.re.reason')} <span className="text-nav-red normal-case">{t('f5r4a.re.required')}</span>
          </span>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('f5r4a.re.reasonPh')}
            className="mt-[7px]"
          />
        </label>

        {/* F5-P2 «Pasar TODOS» — vive SOLO en el camino por origen. Mismo
            mecanismo que el R7 de agentes: confirmación con el número exacto
            ANTES, tandas de 200 con progreso visible, verificación final. */}
        {originMode && fromNavigatorId && (
          <div className="mt-5">
            <button
              type="button"
              disabled={
                !navigatorId
                || allBusy
                || (sourceCount?.movableCases ?? 0) === 0
                || reason.trim().length < 3
              }
              onClick={() => setAllConfirm(true)}
              className={cn(
                'w-full min-h-[44px] px-5 rounded-lg border-[1.5px] font-head text-[13.5px] font-bold transition-colors duration-150 motion-reduce:transition-none',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal',
                'bg-white text-nav-navy border-nav-line hover:bg-nav-softer',
                'disabled:opacity-50 disabled:cursor-not-allowed',
              )}
            >
              {allBusy && allProg
                ? t('f5p2.all.progress', { done: allProg.done, total: allProg.total })
                : t('f5p2.all.cta', { n: sourceCount?.movableCases ?? 0 })}
            </button>

            {allConfirm && (
              <div className="mt-3 rounded-[10px] border-[1.5px] border-nav-amber bg-nav-amberTint px-4 py-3">
                <p className="m-0 mb-2 font-head text-[13px] font-bold text-nav-amberText tabular-nums">
                  {t('f5p2.all.confirmT', {
                    n: sourceCount?.movableCases ?? 0,
                    name: sources.find((s) => String(s.navigatorId) === String(fromNavigatorId))
                      ?.navigatorName ?? '',
                  })}
                </p>
                {/* E-36: lo que se mueve depende de si el origen está activo
                    o de baja (E-35d). La frase única de antes («los que
                    tienen cita jamás se mueven») era falsa para las bajas. */}
                <p className="m-0 mb-2.5 text-[12.5px] text-nav-amberText" data-testid="e36-all-confirm-body">
                  {fromInactive ? t('e36.all.confirmBInactive') : t('e36.all.confirmBActive')}
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => void pasarTodos()}
                    className="min-h-[44px] px-5 rounded-lg bg-nav-navy text-white border-[1.5px] border-nav-navy font-head text-[13.5px] font-bold hover:bg-nav-navyHover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
                  >
                    {t('f5p2.all.confirmGo', { n: sourceCount?.movableCases ?? 0 })}
                  </button>
                  <button
                    type="button"
                    onClick={() => setAllConfirm(false)}
                    className="min-h-[44px] px-5 rounded-lg bg-white text-nav-navy border-[1.5px] border-nav-line font-head text-[13.5px] font-bold hover:bg-nav-softer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
                  >
                    {t('f5p2.all.confirmNo')}
                  </button>
                </div>
              </div>
            )}

            {allBusy && allProg && (
              <p className="m-0 mt-2 text-[12.5px] text-nav-slate tabular-nums" aria-live="polite">
                {t('f5p2.all.progress', { done: allProg.done, total: allProg.total })}
              </p>
            )}
          </div>
        )}
      </section>

      {/* ------------------------------------------------------ 2 · Preview */}
      <section className="rounded-xl border-[1.5px] border-nav-card bg-white p-[22px]">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <p className="font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-teal">
            {t('f5r4a.re.k2')}
          </p>
          {previewFresh && (
            <span className="rounded-full px-2.5 py-1 bg-nav-sageTint text-nav-sageText border border-nav-sage font-head text-[11px] font-bold">
              {t('f5r4a.re.fresh')}
            </span>
          )}
        </div>

        {/* El resultado del preview se anuncia (dibujo: aria-live). */}
        <div aria-live="polite">
          {!previewFresh ? (
            <div className="text-center px-2.5 py-5">
              <span className="inline-flex items-center justify-center w-[46px] h-[46px] rounded-full bg-nav-sageTint text-nav-sageText">
                <Eye className="h-[22px] w-[22px]" aria-hidden />
              </span>
              <p className="mt-3.5 mb-2 font-head text-[15px] font-bold text-nav-navy">
                {t('f5r4a.re.noPrevTitle')}
              </p>
              <p className="max-w-[320px] mx-auto mb-5 text-[13.5px] leading-relaxed text-nav-slate">
                {t('f5r4a.re.noPrevBody')}
              </p>
              <button
                type="button"
                disabled={!canRunPreview}
                onClick={() => previewMut.mutate()}
                className={cn(
                  'min-h-[44px] px-5 rounded-lg border-[1.5px] font-head text-[13.5px] font-bold transition-colors duration-150 motion-reduce:transition-none',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal',
                  canRunPreview
                    ? 'bg-nav-navy text-white border-nav-navy hover:bg-nav-navyHover cursor-pointer'
                    : 'bg-nav-soft text-nav-faint border-nav-card cursor-not-allowed',
                )}
              >
                {previewMut.isPending && <Loader2 className="h-4 w-4 animate-spin inline mr-2" aria-hidden />}
                {t('f5r4a.re.run')}
              </button>
              {previewWhy && (
                <p className="mt-3 text-[12.5px] text-nav-red" role="status">{previewWhy}</p>
              )}
              {previewMut.isError && (
                <p className="mt-3 text-xs text-nav-red" role="alert">
                  {t('adminNav.bulkReassign.previewFailed')}{' '}
                  {navError(previewMut.error).message ?? t('adminNav.bulkReassign.previewFailedGeneric')}
                </p>
              )}
            </div>
          ) : preview && (
            <>
              <div className="flex gap-6 flex-wrap mb-[18px]">
                <span>
                  <span className="block font-head text-[30px] font-extrabold leading-none tracking-[-0.02em] tabular-nums text-nav-navy">
                    {preview.willAssign}
                  </span>
                  <span className="block mt-1.5 text-[12.5px] text-nav-slate">
                    {/* E-36: la frase dice el número y el destino. */}
                    {t('f5r4a.re.move', { n: preview.willAssign, name: preview.navigatorName || destName })}
                  </span>
                </span>
                <span>
                  <span className="block font-head text-[30px] font-extrabold leading-none tracking-[-0.02em] tabular-nums text-nav-amberText">
                    {/* En modo ORIGEN el número grande cuenta TODOS los
                        salteados, no solo las sesiones abiertas: si no, decía
                        0 mientras el detalle de abajo listaba citas y
                        seguimientos. Un número no puede contradecir a su
                        propio detalle. Fuera del modo origen se conserva la
                        conducta que ya está en producción. */}
                    {preview.fromNavigatorId
                      ? preview.sourceOpenCases - preview.movableCases
                      : preview.skippedOpenSession}
                  </span>
                  <span className="block mt-1.5 text-[12.5px] text-nav-amberText">{t('f5r4a.re.skip')}</span>
                </span>
              </div>

              {/* (5) Los salteados con razón, parte del preview. Agregados
                  vivos del server; el detalle por persona NECESITA-SERVER. */}
              <div className="rounded-xl border-[1.5px] border-nav-amber bg-nav-amberTint px-[18px] py-4 text-nav-amberText">
                <p className="font-head text-[10.5px] font-bold tracking-[0.12em] uppercase mb-2.5">
                  {t('f5r4a.re.skippedKicker')}
                </p>
                <div className="flex flex-col gap-1.5 text-[12.5px]">
                  <span className="tabular-nums">{t('f5r4a.re.candidates', { n: preview.candidateCount })}</span>
                  {/* F5-P2: con origen, cada exclusión dice su razón (espejo de
                      los `skipped[]` con `ReassignSkipReason` de agentes). */}
                  {preview.fromNavigatorId ? (
                    <>
                      <span className="tabular-nums">
                        {t('f5p2.re.fromLine', {
                          name: preview.fromNavigatorName ?? '',
                          n: preview.sourceOpenCases,
                        })}
                      </span>
                      {/* E-36: de una persona ACTIVA lo trabajado se queda con
                          ella (MEMBER_HAS_WORK, E-35d). Es el motivo que más
                          pesa y hasta hoy no se veía. */}
                      {(preview.skippedByReason.MEMBER_HAS_WORK ?? 0) > 0 && (
                        <span className="tabular-nums font-semibold" data-testid="e36-stay-worked">
                          {t('e36.re.stayWorked', {
                            name: preview.fromNavigatorName ?? '',
                            n: preview.skippedByReason.MEMBER_HAS_WORK,
                          })}
                        </span>
                      )}
                      {preview.skippedByReason.NAV_APPOINTMENT_SCHEDULED > 0 && (
                        <span className="tabular-nums">
                          {t('f5p2.skip.NAV_APPOINTMENT_SCHEDULED', {
                            n: preview.skippedByReason.NAV_APPOINTMENT_SCHEDULED,
                          })}
                        </span>
                      )}
                      {preview.skippedByReason.FOLLOWUP_ACCEPTED_PENDING > 0 && (
                        <span className="tabular-nums">
                          {t('f5p2.skip.FOLLOWUP_ACCEPTED_PENDING', {
                            n: preview.skippedByReason.FOLLOWUP_ACCEPTED_PENDING,
                          })}
                        </span>
                      )}
                      {preview.skippedByReason.OPEN_SESSION > 0 && (
                        <span className="tabular-nums">
                          {t('f5p2.skip.OPEN_SESSION', {
                            n: preview.skippedByReason.OPEN_SESSION,
                          })}
                        </span>
                      )}
                      {preview.skippedByReason.MEMBER_SPLIT_BY_FILTER > 0 && (
                        <span className="tabular-nums">
                          {t('f5p2.skip.MEMBER_SPLIT_BY_FILTER', {
                            n: preview.skippedByReason.MEMBER_SPLIT_BY_FILTER,
                          })}
                        </span>
                      )}
                      {preview.sourceOpenCases === preview.movableCases && (
                        <span>{t('f5r4a.re.noneSkipped')}</span>
                      )}
                    </>
                  ) : preview.skippedOpenSession > 0 ? (
                    <span className="tabular-nums">
                      {t('adminNav.bulkReassign.skippedSessions', { n: preview.skippedOpenSession })}
                    </span>
                  ) : (
                    <span>{t('f5r4a.re.noneSkipped')}</span>
                  )}
                  {preview.filter === 'findhelp_negatives' && preview.negativesWithoutCase > 0 && (
                    <span className="tabular-nums">
                      {t('adminNav.bulkReassign.negativesInfo', { n: preview.negativesWithoutCase })}
                    </span>
                  )}
                  <span className="opacity-80 text-[11px]">{t('f5r4a.re.skipNamesNa')}</span>
                </div>
              </div>

              {'applied' in preview && typeof (preview as BulkReassignApplyResult).applied === 'number' && (
                <p className="mt-3 text-sm font-medium text-nav-navy">
                  {t('adminNav.bulkReassign.appliedLine', {
                    count: (preview as BulkReassignApplyResult).applied,
                  })}
                </p>
              )}

              {applyBlockReason === 'nothingToAssign' && (
                <p className="mt-3 text-xs text-nav-slate" role="status">
                  {/* El cero mas confuso: hay casos en el filtro pero YA son
                      del destino elegido. Sin esta linea parecia que el
                      contador de arriba estaba viejo (lo cruzo el CEO). */}
                  {preview.alreadyTarget > 0 && preview.candidateCount === 0
                    ? t('f5p2.re.allAlreadyTarget', {
                      n: preview.alreadyTarget,
                      name: preview.navigatorName || destName,
                    })
                    : t('adminNav.bulkReassign.applyNothingToAssign')}
                </p>
              )}

              <div className="flex justify-end gap-2.5 mt-[18px] flex-wrap">
                <Button variant="ghost" onClick={() => setPreview(null)}>
                  {t('f5r4a.re.discard')}
                </Button>
                {/* El confirm nativo se conserva (candado vivo previo al POST;
                    el dibujo ni lo dibuja ni lo mata — E-23). */}
                <Button
                  /* `allBusy` también apaga este botón: «Pasar TODOS» corre
                     por tandas y deja la pantalla viva. Sin esta guarda un
                     click acá dispara una tanda CONCURRENTE sobre la misma
                     cola — dos movimientos pisándose sobre gente real. */
                  disabled={!canApply || applyMut.isPending || allBusy}
                  onClick={() => {
                    if (
                      window.confirm(
                        t('adminNav.bulkReassign.confirm', { count: preview.willAssign }),
                      )
                    ) {
                      applyMut.mutate();
                    }
                  }}
                >
                  {applyMut.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
                  {t('f5r4a.re.assign', { n: preview.willAssign })}
                </Button>
              </div>
            </>
          )}
        </div>
      </section>
      </div>
    </div>
  );
}
